import {
  createSlice,
  current,
  PayloadAction,
  createSelector,
} from "@reduxjs/toolkit";
import {
  DynamicSeatCount,
  GameFilters,
  InventoryFilters,
  SectionName,
} from "./types";
import { GlobalState } from "../store";
import { Router } from "next/router";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import { createDynamicSeatCountPayload } from "@/global/api/api.utils";

interface Payload<T> {
  keys: string[];
  value: T;
}

const initialState: GameFilters = {
  seatCount: 2,
  isInitialSeatCountHydrated: false,
  dynamicSeatCount: [],
  maxSeatCount: 2,
  priceRange: {
    data: { min: 1, max: 1000 },
    selected: [1, 1000],
  },
  accessibility: {
    isAda: false,
  },
  eventCodes: [],
  dates: [],
  opponents: {
    data: [],
    selected: [],
  },
  sections: {
    data: [],
    selected: null,
  },
  categories: {
    data: [],
    selected: [],
    temporaryData: [],
  },
};

export const gameFiltersState = createSlice({
  name: "eventFilters",
  initialState,
  reducers: {
    setByKeys: <T, K = GameFilters>(
      state: K,
      action: PayloadAction<Payload<T>>
    ) => {
      let lastKey: any = action.payload.keys.pop();
      let next: any = state;
      action.payload.keys.forEach((key) => (next = next[key]));
      next[lastKey] = action.payload.value;
    },

    set: (state, action: PayloadAction<GameFilters>) => {
      return { ...state, ...action.payload };
    },

    updateInventoryFilters: (
      state,
      action: PayloadAction<{
        priceRange?: GameFilters["priceRange"];
        sections?: GameFilters["sections"];
        seatCount?: GameFilters["seatCount"];
        dynamicSeatCount?: GameFilters["dynamicSeatCount"];
        maxSeatCount?: GameFilters["maxSeatCount"];
      }>
    ) => {
      const {
        priceRange,
        seatCount,
        sections,
        dynamicSeatCount,
        maxSeatCount,
      } = action.payload;
      if (priceRange) state.priceRange = priceRange;
      if (seatCount !== undefined) state.seatCount = seatCount;
      if (sections) state.sections = sections;
      if (dynamicSeatCount !== undefined)
        state.dynamicSeatCount = dynamicSeatCount;
      if (maxSeatCount !== undefined) state.maxSeatCount = maxSeatCount;
    },

    updatePriceRangeState: (
      state,
      action: PayloadAction<GameFilters["priceRange"]>
    ) => {
      state.priceRange = action.payload;
    },

    updateEventCodes: (state, action: PayloadAction<string[]>) => {
      state.eventCodes = action.payload;
    },
    updateDatesSelection: (state, action: PayloadAction<string[]>) => {
      state.dates = action.payload;
    },
    updateOpponentSelection: (state, action: PayloadAction<string>) => {
      let opName = action.payload;
      let selected = state.opponents.selected;
      if (selected.includes(opName)) {
        state.opponents.selected = selected.filter((itm) => itm !== opName);
      } else {
        state.opponents.selected = [...selected, opName];
      }
    },
    updateCategorySelection: (state, action: PayloadAction<string>) => {
      let catName = action.payload;
      let selected = state.categories.selected || [];
      if (selected.includes(catName)) {
        state.categories.selected = selected.filter((itm) => itm !== catName);
      } else {
        state.categories.selected = [...selected, catName];
      }
    },
    updateSectionSelected: (
      state,
      action: PayloadAction<SectionName | null>
    ) => {
      state.sections.selected = action.payload;
    },
    setSeatCount: (state, action: PayloadAction<number>) => {
      state.seatCount = action.payload;
    },
    setInitialSeatCountHydrated: (state, action: PayloadAction<boolean>) => {
      state.isInitialSeatCountHydrated = action.payload;
    },

    setDynamicSeatCount: (state, action: PayloadAction<DynamicSeatCount[]>) => {
      state.dynamicSeatCount = action.payload;

      // updating the total seat count to the seatCount state
      const totalSeatCount = action.payload.reduce((total, seatCount) => {
        return total + seatCount.seatCount;
      }, 0);
      state.seatCount = totalSeatCount;
    },

    setDynamicSeatCountItem: (
      state,
      action: PayloadAction<DynamicSeatCount>
    ) => {
      const { id, seatCount, orderIndex, isDisabled } = action.payload;

      const seatCounter = state.dynamicSeatCount.find((item) => item.id === id);
      if (seatCounter) {
        seatCounter.seatCount = seatCount;
        if (orderIndex !== undefined) seatCounter.orderIndex = orderIndex;
        if (isDisabled !== undefined) seatCounter.isDisabled = isDisabled;
      } else {
        // If not found, add new item with required fields
        state.dynamicSeatCount.push({
          id,
          seatCount,
          orderIndex: orderIndex || 0,
          isDisabled: isDisabled || false,
        });
      }

      // updating the total seat count to the seatCount state
      const totalSeatCount = state.dynamicSeatCount.reduce(
        (total, seatCount) => {
          return total + seatCount.seatCount;
        },
        0
      );
      state.seatCount = totalSeatCount;
    },

    clearEventFilters: (state) => {
      state.dates = [];
      state.opponents.selected = [];
    },
  },
});

// selectors
const selector = (state: GlobalState) => state.data.gameFilters;

export const selectSeatCount = createSelector(selector, (s) => s.seatCount);
export const selectDates = createSelector(selector, (s) => s.dates);
export const selectOpponents = createSelector(
  selector,
  (s) => s.opponents.data
);
export const selectOpponentsSelected = createSelector(
  selector,
  (s) => s.opponents.selected
);

export const selectPriceRangeState = createSelector(
  selector,
  (s) => s.priceRange
);
export const selectSectionsState = createSelector(selector, (s) => s.sections);
export const selectPriceRange = createSelector(
  selector,
  (s) => s.priceRange.data
);
export const selectPriceRangeSelected = createSelector(
  selector,
  (s) => s.priceRange.selected
);
export const selectAccessibility = createSelector(
  selector,
  (s) => s.accessibility
);
export const selectEventCodes = createSelector(
  selector,
  (choseGames) => choseGames.eventCodes
);
export const selectSections = createSelector(selector, (s) => s.sections.data);
export const selectSectionSelected = createSelector(
  selector,
  (s) => s?.sections?.selected
);
export const selectIsAda = createSelector(
  selector,
  (s) => s.accessibility.isAda
);
export const selectCategories = createSelector(
  selector,
  (s) => s.categories?.data
);
export const selectTemporaryCategories = createSelector(
  selector,
  (s) => s.categories?.temporaryData
);
export const selectCategoriesSelected = createSelector(
  selector,
  (s) => s.categories?.selected
);

export const selectDynamicSeatCount = createSelector(
  selector,
  (s) => s.dynamicSeatCount
);

export const selectMaxSeatCount = createSelector(
  selector,
  (s) => s.maxSeatCount
);

export const selectInventoryFilters = (teamConfig: TeamConfig) => {
  return createSelector(selector, (s) => {
    // Base filters that are always included
    const filters: InventoryFilters = {
      seatCount: s.seatCount,
      priceRange: s.priceRange.selected,
      sectionNames: (s.sections.selected?.sectionNames || []) as string[],
      inventoryType: s.accessibility.isAda ? "ADA" : teamConfig.inventoryType,
    };

    const dynamicSeatCount = createDynamicSeatCountPayload({
      teamConfig,
      seatCountArr: s.dynamicSeatCount,
    });

    if (dynamicSeatCount?.length) {
      filters.dynamicSeatCount = dynamicSeatCount;
    }

    return filters;
  });
};

export const selectIsInitialSeatCountHydrated = createSelector(
  selector,
  (s) => s.isInitialSeatCountHydrated
);

export const {
  updateOpponentSelection,
  updateDatesSelection,
  updateEventCodes,
  updateCategorySelection,
  setDynamicSeatCount,
  setDynamicSeatCountItem,
} = gameFiltersState.actions;
export default gameFiltersState.reducer;
